import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sparkles,
  X,
  Loader2,
  Check,
  Ban,
  MessageSquareQuote,
  Save,
  RefreshCw,
  Eye,
  BookOpen,
  Gauge,
  Smile,
  Volume2,
  ThumbsUp,
  ThumbsDown,
  PenTool,
} from 'lucide-react';
import { Modal, Button, Input, Textarea } from '@mellify/ui';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { useToast } from '../../../../hooks/useToast';
import {
  brandVoiceService,
  type AIGenerateBrandVoiceRequest,
  type AIGenerateBrandVoiceResult,
  type CreateBrandVoicePayload,
} from '../../../../services/brandVoiceService';
import { buildRoute } from '../../../../routes/routeConfig';

interface AIGenerateBrandVoiceModalProps {
  open: boolean;
  onClose: () => void;
}

const TONE_OPTIONS = [
  'professional',
  'casual',
  'friendly',
  'authoritative',
  'humorous',
  'inspirational',
  'empathetic',
  'bold',
  'witty',
  'conversational',
];

/** Human-readable labels for styleGuidelines keys */
const STYLE_GUIDELINE_LABELS: Record<string, string> = {
  writingStyle: 'Writing Style',
  sentenceStructure: 'Sentence Structure',
  paragraphStyle: 'Paragraph Style',
  punctuationStyle: 'Punctuation Style',
  capitalizationRules: 'Capitalization Rules',
  formattingPreferences: 'Formatting Preferences',
  ctaStyle: 'Call-to-Action Style',
  contentLength: 'Content Length',
  openingHooks: 'Opening Hooks',
  closingPatterns: 'Closing Patterns',
};

export const AIGenerateBrandVoiceModal: React.FC<AIGenerateBrandVoiceModalProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const { addToast } = useToast();

  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [toneHints, setToneHints] = useState<string[]>([]);
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [saveImmediately, setSaveImmediately] = useState(false);
  const [result, setResult] = useState<AIGenerateBrandVoiceResult | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setName('');
    setIndustry('');
    setTargetAudience('');
    setToneHints([]);
    setAdditionalInstructions('');
    setSaveImmediately(false);
    setResult(null);
    setSavedId(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const generateMutation = useMutation({
    mutationFn: (data: AIGenerateBrandVoiceRequest) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      return brandVoiceService.aiGenerate(currentWorkspace.id, data);
    },
    onSuccess: (data) => {
      setResult(data);
      if (data.saved && data.id) {
        setSavedId(data.id);
        queryClient.invalidateQueries({ queryKey: ['brandVoices'] });
        addToast({
          type: 'success',
          title: 'Brand Voice Generated & Saved',
          message: data.message,
        });
      } else {
        addToast({
          type: 'success',
          title: 'Brand Voice Generated',
          message: data.message,
        });
      }
    },
    onError: (error: Error) => {
      addToast({
        type: 'error',
        title: 'Generation Failed',
        message: error.message || 'Failed to generate brand voice. Please try again.',
      });
    },
  });

  const saveMutation = useMutation({
    mutationFn: (payload: CreateBrandVoicePayload) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      return brandVoiceService.create(currentWorkspace.id, payload);
    },
    onSuccess: (data) => {
      setSavedId(data.id);
      queryClient.invalidateQueries({ queryKey: ['brandVoices'] });
      addToast({
        type: 'success',
        title: 'Brand Voice Saved',
        message: `"${data.name}" has been saved to your workspace.`,
      });
    },
    onError: (error: Error) => {
      addToast({
        type: 'error',
        title: 'Save Failed',
        message: error.message || 'Failed to save brand voice. Please try again.',
      });
    },
  });

  const handleGenerate = useCallback(() => {
    const payload: AIGenerateBrandVoiceRequest = {};
    if (name.trim()) payload.name = name.trim();
    if (industry.trim()) payload.industry = industry.trim();
    if (targetAudience.trim()) payload.targetAudience = targetAudience.trim();
    if (toneHints.length > 0) payload.toneHints = toneHints;
    if (additionalInstructions.trim()) payload.additionalInstructions = additionalInstructions.trim();
    payload.saveImmediately = saveImmediately;

    generateMutation.mutate(payload);
  }, [name, industry, targetAudience, toneHints, additionalInstructions, saveImmediately, generateMutation]);

  const handleToneToggle = useCallback((tone: string) => {
    setToneHints((prev) =>
      prev.includes(tone) ? prev.filter((t) => t !== tone) : [...prev, tone]
    );
  }, []);

  const handleAcceptAndSave = useCallback(() => {
    if (!result) return;

    const bv = result.brandVoice;
    const payload: CreateBrandVoicePayload = {
      name: bv.name,
      description: bv.description,
      tone: bv.tone as CreateBrandVoicePayload['tone'],
      formalityLevel: bv.formalityLevel as CreateBrandVoicePayload['formalityLevel'],
      emojiUsage: bv.emojiUsage as CreateBrandVoicePayload['emojiUsage'],
      dos: bv.dos,
      donts: bv.donts,
      samplePosts: bv.samplePosts.map((content) => ({ content, platform: 'general' })),
      isDefault: false,
    };

    // Include style guidelines from AI generation
    if (bv.styleGuidelines && Object.keys(bv.styleGuidelines).length > 0) {
      payload.styleGuidelines = bv.styleGuidelines;
    }

    // Include hashtag preferences from AI generation
    if (bv.hashtagPreferences && Object.keys(bv.hashtagPreferences).length > 0) {
      payload.hashtagPreferences = bv.hashtagPreferences;
    }

    // Include vocabulary preferences from AI generation
    if (bv.vocabularyPreferences && Object.keys(bv.vocabularyPreferences).length > 0) {
      payload.vocabularyPreferences = bv.vocabularyPreferences as CreateBrandVoicePayload['vocabularyPreferences'];
    }

    if (bv.mandatoryDisclaimers && bv.mandatoryDisclaimers.length > 0) {
      payload.mandatoryDisclaimers = bv.mandatoryDisclaimers.map((text) => ({ text }));
    }

    saveMutation.mutate(payload);
  }, [result, saveMutation]);

  const handleViewResult = useCallback(() => {
    const id = savedId || result?.id;
    if (id) {
      handleClose();
      navigate(buildRoute.brandVoiceEdit(id));
    }
  }, [savedId, result, handleClose, navigate]);

  const handleGenerateAnother = useCallback(() => {
    setResult(null);
    setSavedId(null);
  }, []);

  const isGenerating = generateMutation.isPending;
  const isSaving = saveMutation.isPending;
  const isSaved = !!(savedId || result?.saved);

  /** Render style guidelines entries as a list of key-value pairs */
  const renderStyleGuidelines = (guidelines: Record<string, unknown>) => {
    const entries = Object.entries(guidelines).filter(
      ([, value]) => typeof value === 'string' && (value as string).trim().length > 0
    );
    if (entries.length === 0) return null;

    return (
      <div className="space-y-2.5">
        {entries.map(([key, value]) => (
          <div key={key}>
            <p className="text-[11px] font-semibold text-dark-500 dark:text-dark-400 uppercase tracking-wider mb-0.5">
              {STYLE_GUIDELINE_LABELS[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
            </p>
            <p className="text-xs text-dark-700 dark:text-dark-300 leading-relaxed">
              {value as string}
            </p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal open={open} onClose={handleClose} showCloseButton={false}>
      <div className="w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-dark-900 px-6 pt-6 pb-4 border-b border-dark-100 dark:border-dark-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center ring-1 ring-primary/20">
                <Sparkles className="w-4.5 h-4.5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-dark-900 dark:text-dark-50">
                  AI Generate Brand Voice
                </h2>
                <p className="text-sm text-dark-500 dark:text-dark-400">
                  {!result
                    ? 'Let AI create a brand voice profile using your knowledge base'
                    : 'Review your AI-generated brand voice profile'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-1.5 rounded-lg text-dark-400 hover:text-dark-600 dark:hover:text-dark-300 hover:bg-dark-100 dark:hover:bg-dark-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          {!result ? (
            /* Form */
            <div className="flex flex-col gap-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
                  Brand Voice Name
                  <span className="text-dark-400 font-normal"> (optional)</span>
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Corporate Professional, Friendly Startup"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isGenerating}
                />
              </div>

              {/* Industry */}
              <div>
                <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
                  Industry
                  <span className="text-dark-400 font-normal"> (optional)</span>
                </label>
                <Input
                  type="text"
                  placeholder="e.g., SaaS, E-commerce, Healthcare"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  disabled={isGenerating}
                />
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
                  Target Audience
                  <span className="text-dark-400 font-normal"> (optional)</span>
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Small business owners, Tech-savvy millennials"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  disabled={isGenerating}
                />
              </div>

              {/* Tone Hints */}
              <div>
                <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
                  Tone Hints
                  <span className="text-dark-400 font-normal"> (select one or more)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {TONE_OPTIONS.map((tone) => (
                    <button
                      key={tone}
                      type="button"
                      onClick={() => handleToneToggle(tone)}
                      disabled={isGenerating}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        toneHints.includes(tone)
                          ? 'bg-primary/10 border-primary text-primary'
                          : 'bg-white dark:bg-dark-800 border-dark-200 dark:border-dark-600 text-dark-600 dark:text-dark-400 hover:border-dark-300 dark:hover:border-dark-500'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {tone}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Instructions */}
              <div>
                <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
                  Additional Instructions
                  <span className="text-dark-400 font-normal"> (optional)</span>
                </label>
                <Textarea
                  placeholder="Any specific guidelines or preferences for the AI to follow..."
                  value={additionalInstructions}
                  onChange={(e) => setAdditionalInstructions(e.target.value)}
                  rows={3}
                  disabled={isGenerating}
                />
              </div>

              {/* Save Immediately Toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveImmediately}
                  onChange={(e) => setSaveImmediately(e.target.checked)}
                  disabled={isGenerating}
                  className="w-4 h-4 rounded border-dark-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-dark-600 dark:text-dark-400">
                  Save profile immediately after generation
                </span>
              </label>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClose}
                  disabled={isGenerating}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={
                    isGenerating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )
                  }
                  onClick={handleGenerate}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Generate Brand Voice'}
                </Button>
              </div>
            </div>
          ) : (
            /* Result Preview */
            <div className="flex flex-col gap-5">
              {/* Confidence Score Banner */}
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-success/10 via-success/5 to-transparent border border-success/20 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-success/15 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-success" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-dark-800 dark:text-dark-100">
                      Brand voice generated successfully!
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center gap-2 flex-1">
                        <div className="flex-1 h-2 bg-dark-200 dark:bg-dark-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-success to-emerald-400 rounded-full transition-all duration-700"
                            style={{ width: `${Math.round(result.confidenceScore * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-bold text-success tabular-nums">
                          {Math.round(result.confidenceScore * 100)}%
                        </span>
                      </div>
                      <span className="text-xs text-dark-400 dark:text-dark-500 whitespace-nowrap">
                        confidence
                      </span>
                    </div>
                    {result.sourcesUsed.length > 0 && (
                      <p className="text-xs text-dark-500 dark:text-dark-400 mt-1.5 flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        {result.sourcesUsed.length} knowledge source{result.sourcesUsed.length !== 1 ? 's' : ''} referenced
                      </p>
                    )}
                  </div>
                </div>
                {isSaved && (
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success/15 text-success ring-1 ring-success/20">
                      <Check className="w-3 h-3" />
                      Saved
                    </span>
                  </div>
                )}
              </div>

              {/* Name & Description Card */}
              <div className="rounded-xl border border-dark-200 dark:border-dark-700 bg-white dark:bg-dark-800/50 overflow-hidden">
                <div className="px-4 py-3 bg-dark-50 dark:bg-dark-800 border-b border-dark-200 dark:border-dark-700">
                  <h3 className="text-sm font-semibold text-dark-800 dark:text-dark-100">
                    {result.brandVoice.name}
                  </h3>
                </div>
                <div className="px-4 py-3">
                  <p className="text-sm text-dark-600 dark:text-dark-300 leading-relaxed">
                    {result.brandVoice.description}
                  </p>
                </div>
              </div>

              {/* Voice Attributes */}
              <div className="grid grid-cols-3 gap-3">
                {/* Tone */}
                <div className="rounded-xl border border-dark-200 dark:border-dark-700 bg-white dark:bg-dark-800/50 p-3 text-center">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-2">
                    <Volume2 className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-[10px] font-medium text-dark-400 dark:text-dark-500 uppercase tracking-wider mb-0.5">
                    Tone
                  </p>
                  <p className="text-sm font-semibold text-dark-800 dark:text-dark-100 capitalize">
                    {result.brandVoice.tone}
                  </p>
                </div>

                {/* Formality */}
                <div className="rounded-xl border border-dark-200 dark:border-dark-700 bg-white dark:bg-dark-800/50 p-3 text-center">
                  <div className="w-8 h-8 rounded-lg bg-info/10 flex items-center justify-center mx-auto mb-2">
                    <Gauge className="w-4 h-4 text-info" />
                  </div>
                  <p className="text-[10px] font-medium text-dark-400 dark:text-dark-500 uppercase tracking-wider mb-0.5">
                    Formality
                  </p>
                  <p className="text-sm font-semibold text-dark-800 dark:text-dark-100 capitalize">
                    {result.brandVoice.formalityLevel.replace('_', ' ')}
                  </p>
                </div>

                {/* Emoji */}
                <div className="rounded-xl border border-dark-200 dark:border-dark-700 bg-white dark:bg-dark-800/50 p-3 text-center">
                  <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center mx-auto mb-2">
                    <Smile className="w-4 h-4 text-warning" />
                  </div>
                  <p className="text-[10px] font-medium text-dark-400 dark:text-dark-500 uppercase tracking-wider mb-0.5">
                    Emoji Usage
                  </p>
                  <p className="text-sm font-semibold text-dark-800 dark:text-dark-100 capitalize">
                    {result.brandVoice.emojiUsage}
                  </p>
                </div>
              </div>

              {/* Writing Style Guidelines */}
              {result.brandVoice.styleGuidelines &&
                Object.keys(result.brandVoice.styleGuidelines).length > 0 && (
                  <div className="rounded-xl border border-dark-200 dark:border-dark-700 bg-white dark:bg-dark-800/50 overflow-hidden">
                    <div className="px-4 py-2.5 bg-dark-50 dark:bg-dark-800 border-b border-dark-200 dark:border-dark-700 flex items-center gap-2">
                      <PenTool className="w-3.5 h-3.5 text-dark-500" />
                      <span className="text-xs font-semibold text-dark-600 dark:text-dark-300 uppercase tracking-wider">
                        Writing Style Guidelines
                      </span>
                    </div>
                    <div className="px-4 py-3">
                      {renderStyleGuidelines(result.brandVoice.styleGuidelines)}
                    </div>
                  </div>
                )}

              {/* Do's and Don'ts */}
              {(result.brandVoice.dos.length > 0 || result.brandVoice.donts.length > 0) && (
                <div className="grid grid-cols-2 gap-3">
                  {/* Do's */}
                  {result.brandVoice.dos.length > 0 && (
                    <div className="rounded-xl border border-success/20 bg-success/5 dark:bg-success/5 overflow-hidden">
                      <div className="px-3 py-2 border-b border-success/15 flex items-center gap-2">
                        <ThumbsUp className="w-3.5 h-3.5 text-success" />
                        <span className="text-xs font-semibold text-success uppercase tracking-wider">
                          Do's ({result.brandVoice.dos.length})
                        </span>
                      </div>
                      <div className="px-3 py-2.5 space-y-1.5">
                        {result.brandVoice.dos.map((item, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <Check className="w-3.5 h-3.5 text-success flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-dark-700 dark:text-dark-300 leading-relaxed">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Don'ts */}
                  {result.brandVoice.donts.length > 0 && (
                    <div className="rounded-xl border border-danger/20 bg-danger/5 dark:bg-danger/5 overflow-hidden">
                      <div className="px-3 py-2 border-b border-danger/15 flex items-center gap-2">
                        <ThumbsDown className="w-3.5 h-3.5 text-danger" />
                        <span className="text-xs font-semibold text-danger uppercase tracking-wider">
                          Don'ts ({result.brandVoice.donts.length})
                        </span>
                      </div>
                      <div className="px-3 py-2.5 space-y-1.5">
                        {result.brandVoice.donts.map((item, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <Ban className="w-3.5 h-3.5 text-danger flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-dark-700 dark:text-dark-300 leading-relaxed">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Sample Posts */}
              {result.brandVoice.samplePosts.length > 0 && (
                <div className="rounded-xl border border-dark-200 dark:border-dark-700 bg-white dark:bg-dark-800/50 overflow-hidden">
                  <div className="px-4 py-2.5 bg-dark-50 dark:bg-dark-800 border-b border-dark-200 dark:border-dark-700 flex items-center gap-2">
                    <MessageSquareQuote className="w-3.5 h-3.5 text-dark-500" />
                    <span className="text-xs font-semibold text-dark-600 dark:text-dark-300 uppercase tracking-wider">
                      Sample Posts
                    </span>
                  </div>
                  <div className="p-4 space-y-3">
                    {result.brandVoice.samplePosts.map((post, i) => (
                      <div
                        key={i}
                        className="relative pl-4 border-l-2 border-primary/30"
                      >
                        <p className="text-sm text-dark-700 dark:text-dark-200 leading-relaxed italic">
                          "{post}"
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Knowledge Sources */}
              {result.sourcesUsed.length > 0 && (
                <div className="rounded-xl border border-dark-200 dark:border-dark-700 bg-dark-50 dark:bg-dark-800/80 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-3.5 h-3.5 text-dark-500" />
                    <span className="text-[10px] font-semibold text-dark-500 dark:text-dark-400 uppercase tracking-wider">
                      Knowledge Sources Used
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {result.sourcesUsed.map((source) => (
                      <span
                        key={source.documentId}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs bg-white dark:bg-dark-700 border border-dark-200 dark:border-dark-600 text-dark-600 dark:text-dark-300"
                      >
                        {source.documentTitle}
                        {source.chunkCount > 0 && (
                          <span className="text-dark-400 dark:text-dark-500">
                            ({source.chunkCount})
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between gap-3 pt-1 border-t border-dark-100 dark:border-dark-800">
                <Button variant="outline" size="sm" onClick={handleClose}>
                  Close
                </Button>
                <div className="flex items-center gap-2">
                  {/* Generate Another */}
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<RefreshCw className="w-4 h-4" />}
                    onClick={handleGenerateAnother}
                    disabled={isSaving}
                  >
                    Regenerate
                  </Button>

                  {/* Accept & Save (when not saved yet) */}
                  {!isSaved && (
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={
                        isSaving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )
                      }
                      onClick={handleAcceptAndSave}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Accept & Save'}
                    </Button>
                  )}

                  {/* View & Edit (when saved) */}
                  {isSaved && (
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<Eye className="w-4 h-4" />}
                      onClick={handleViewResult}
                    >
                      View & Edit Profile
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

AIGenerateBrandVoiceModal.displayName = 'AIGenerateBrandVoiceModal';